import { useMemo } from "react";
import ALIASES_GLOSSARY, { type AliasEntry } from "@/data/aliases_glossary";

function normalize(s: string): string {
  return s.toLowerCase().replace(/[\s\-_]+/g, "");
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Build a fuzzy regex pattern that matches the alias allowing optional
 * spaces/dashes/underscores between original characters.
 * Es. "Hub 2" → /H\W*u\W*b\W*2/i  (così matcha "hub2", "hub-2", "hub 2").
 */
function fuzzyPattern(sigla: string): string {
  // Remove existing separators, keep alphanumerics; insert optional \W* between chars.
  const cleaned = sigla.replace(/[\s\-_]+/g, "");
  const chars = Array.from(cleaned).map(escapeRegex);
  return chars.join("[\\s\\-_]*");
}

export interface AliasMatcher {
  entries: AliasEntry[];
  regex: RegExp | null;
  lookup: Map<string, AliasEntry>;
  findByMatch: (matchText: string) => AliasEntry | undefined;
}

export function useAliasMatcher(): AliasMatcher {
  return useMemo<AliasMatcher>(() => {
    const entries = ALIASES_GLOSSARY;
    if (!entries.length) {
      return { entries, regex: null, lookup: new Map(), findByMatch: () => undefined };
    }

    // Ordina per lunghezza DESC così matcha "Hub 2 Plus" prima di "Hub 2".
    const sorted = [...entries].sort((a, b) => b.sigla.length - a.sigla.length);

    const lookup = new Map<string, AliasEntry>();
    const patterns: string[] = [];
    for (const e of sorted) {
      lookup.set(normalize(e.sigla), e);
      if (e.synonyms) {
        for (const syn of e.synonyms) lookup.set(normalize(syn), e);
      }
      patterns.push(fuzzyPattern(e.sigla));
      if (e.synonyms) {
        for (const syn of e.synonyms) patterns.push(fuzzyPattern(syn));
      }
    }

    // Word boundaries: usiamo lookaround per non spezzare parole tipo "GitHub2"
    // ma permettere match all'inizio/fine stringa o accanto a punteggiatura/spazi.
    const regex = new RegExp(
      `(?<![A-Za-z0-9])(?:${patterns.join("|")})(?![A-Za-z0-9])`,
      "gi",
    );

    const findByMatch = (matchText: string) => lookup.get(normalize(matchText));

    return { entries, regex, lookup, findByMatch };
  }, []);
}